import { useState, useEffect } from 'react';
import {
  Outlet,
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
  createSearchParams,
} from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { get } from 'node-emoji';
import Avatar from '../components/Avatar';
import {
  Chrome,
  Main,
  Meta,
  Nav,
  NavLink,
  NavList,
} from '../components/Chrome';
import {
  Drawer,
  DrawerExit,
  DrawerLink,
  DrawerList,
  DrawerTrigger,
} from '../components/Drawer';
import Global from '../components/Global';
import Icon from '../components/Icon';
import { SPACE, VIEWER } from '../utilities/queries';
import { SyncProvider } from '../contexts/sync';
import { useSpaceContext } from '../contexts/space';
import { useLayerContext } from '../contexts/layer';
import { TOGGLE_NIGHT_MODE } from '../utilities/mutations';

const MainLayout = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const { spaceVars } = useSpaceContext();
  const { layers } = useLayerContext();

  const [keys, setKeys] = useState([]);

  const [toggleNightMode] = useMutation(TOGGLE_NIGHT_MODE);

  const { loading: spaceLoading, data: spaceData } = useQuery(SPACE, {
    variables: {
      slug: params.s,
      ...spaceVars,
    },
    skip: !params.s,
  });
  const { data: viewerData } = useQuery(VIEWER, {
    skip: !localStorage.getItem('token'),
  });

  const [selected, setSelected] = useState(params.s);
  const [isNavListOpen, setIsNavListOpen] = useState(!params.s || false);

  useEffect(() => {
    if (
      localStorage.getItem('token') &&
      params.s &&
      localStorage.getItem('lastSpaceSlug') !== params.s &&
      !spaceLoading &&
      spaceData?.space
    ) {
      localStorage.setItem('lastSpaceSlug', params.s);
    }
  }, [spaceLoading, params.s]);

  // Handle viewer and space keyboard shortcuts
  useEffect(() => {
    const onFocusHandler = () => {
      setKeys([]);
    };

    const onKeyUpHandler = (e) => {
      setKeys([...keys.filter((key) => key !== e.key)]);
    };

    const onKeyDownHandler = (e) => {
      setKeys([...keys, e.key]);

      if (
        !keys.includes('Control') &&
        !keys.includes('Alt') &&
        !keys.includes('v') &&
        e.key === '?' &&
        layers.length === 0
      ) {
        // Keyboard shortcuts

        navigate('/keyboard-shortcuts', {
          state: {
            inApp: true,
            fromSpace: params.s && location.pathname.split('/').splice(1)[1],
            fromTopic: params.t,
            fromSettings: location.pathname.split('/')[1] === 'settings',
            backgroundLocation: location,
          },
        });
      }

      // Viewer

      if (viewerData && viewerData.viewer.spaces && layers.length === 0) {
        if (keys.includes('v')) {
          if (e.key === 'l') {
            // Logout

            e.preventDefault();

            navigate('/logout', {
              state: { inApp: true, backgroundLocation: location },
            });
          } else if (e.key === 's') {
            // Settings

            e.preventDefault();

            navigate('settings');
          } else if (e.key === 'n') {
            // Night mode

            e.preventDefault();

            toggleNightMode();
          }
        }
      }

      // Space

      if (
        !keys.includes('Control') &&
        !keys.includes('Shift') &&
        !keys.includes('Alt') &&
        !keys.includes('v') &&
        params.s &&
        layers.length === 0 &&
        spaceData
      ) {
        if (e.key === '/' && spaceData.space.role) {
          // Search

          e.preventDefault();

          navigate(
            `/s/${params.s}/search${
              searchParams && `?${createSearchParams(searchParams)}`
            }`,
            {
              state: {
                spaceSlug: spaceData.space.slug,
                backgroundLocation: location,
              },
            }
          );
        } else if (e.key === 'h') {
          // Home

          e.preventDefault();
          navigate(`/s/${params.s}`);
        } else if (e.key === 's' && spaceData.space.role === 'ADMIN') {
          // Settings

          e.preventDefault();
          navigate(`/s/${spaceData.space.slug}/settings`);
        } else if (e.key === 'i' && spaceData.space.role === 'ADMIN') {
          // Invite link

          e.preventDefault();
          navigate(`/s/${spaceData.space.slug}/settings/members/invite-link`);
        } else if (e.key === 'n') {
          // New topic

          e.preventDefault();
          navigate(
            `/s/${spaceData.space.slug}/t/new${
              searchParams && `?${createSearchParams(searchParams)}`
            }`,
            {
              state: {
                spaceId: spaceData.space.id,
                spaceSlug: spaceData.space.slug,
                backgroundLocation: location,
              },
            }
          );
        }
      }

      // Space switcher

      if (
        !keys.includes('Control') &&
        !keys.includes('Alt') &&
        !keys.includes('v') &&
        location.pathname.split('/')[1] !== 'settings' &&
        viewerData &&
        viewerData.viewer.spaces &&
        layers.length === 0
      ) {
        if (e.key === '!') {
          setIsNavListOpen(!isNavListOpen);
        }
      }
    };

    window.addEventListener('focus', onFocusHandler);
    document.addEventListener('keyup', onKeyUpHandler);
    document.addEventListener('keydown', onKeyDownHandler);

    return () => {
      window.removeEventListener('focus', onFocusHandler);
      document.removeEventListener('keyup', onKeyUpHandler);
      document.removeEventListener('keydown', onKeyDownHandler);
    };
  }, [keys, params.s, location, layers]);

  useEffect(() => {
    if (!params.s) {
      setIsNavListOpen(true);
    }
  }, [params.s]);

  const firstPathLetter = params.s && params.s[0];

  return (
    <SyncProvider
      viewerId={viewerData?.viewer?.id}
      spaceId={spaceData?.space?.id}
      spaceRole={spaceData?.space?.role}
      spaceSlug={params.s}
    >
      <Global />
      <Chrome>
        <Nav>
          {!spaceLoading && (
            <>
              <NavList
                isOpen={isNavListOpen}
                onMouseOut={() => setSelected(params.s)}
              >
                {viewerData && viewerData.viewer.spaces && isNavListOpen ? (
                  <>
                    {spaceData?.space && !spaceData.space?.role && (
                      <NavLink
                        as="button"
                        key={spaceData.space.id}
                        tooltip={spaceData.space?.name}
                        onClick={() => setIsNavListOpen(!isNavListOpen)}
                        onMouseOver={() => setSelected(spaceData.space.slug)}
                        $isSelected={
                          params.s && selected === spaceData.space.slug
                        }
                      >
                        {spaceData.space.icon
                          ? get(spaceData.space.icon)
                          : spaceData.space.name[0]}
                      </NavLink>
                    )}
                    {[...viewerData.viewer.spaces]
                      .sort((a, b) => {
                        if (a.slug === params.s) {
                          return -1;
                        } else if (b.slug === params.s) {
                          return 1;
                        }
                      })
                      .map((space) => (
                        <NavLink
                          as={
                            spaceData?.space &&
                            space.slug === spaceData.space.slug &&
                            'button'
                          }
                          to={
                            spaceData?.space &&
                            space.slug === spaceData.space.slug
                              ? null
                              : `/s/${space.slug}`
                          }
                          key={space.id}
                          tooltip={space.name}
                          onClick={() => setIsNavListOpen(!isNavListOpen)}
                          onMouseOver={() => setSelected(space.slug)}
                          $isSelected={params.s && selected === space.slug}
                        >
                          {space.icon ? get(space.icon) : space.name[0]}
                        </NavLink>
                      ))}
                    <NavLink
                      to="/s/new"
                      state={{
                        inApp: true,
                        backgroundLocation: location,
                      }}
                      tooltip="New space"
                      onMouseOver={() => setSelected('plus')}
                      $isSelected={selected === 'plus'}
                    >
                      <Icon name="create" />
                    </NavLink>
                  </>
                ) : (
                  <NavLink
                    as="button"
                    tooltip={
                      spaceData?.space?.id &&
                      (spaceData.space.name || firstPathLetter)
                    }
                    onClick={() =>
                      viewerData && setIsNavListOpen(!isNavListOpen)
                    }
                  >
                    {spaceData?.space?.id
                      ? spaceData.space.icon
                        ? get(spaceData.space.icon)
                        : spaceData.space.name[0]
                      : firstPathLetter}
                  </NavLink>
                )}
              </NavList>
              {localStorage.getItem('token') &&
                spaceData &&
                spaceData.space?.role && (
                  <>
                    <NavLink to={`/s/${spaceData.space.slug}`} tooltip="Home">
                      {location.pathname === `/s/${spaceData.space.slug}` ? (
                        <Icon name="home-filled" />
                      ) : (
                        <Icon name="home" />
                      )}
                    </NavLink>
                    <NavLink
                      to={{
                        pathname: `/s/${spaceData.space.slug}/search`,
                        search: `?${createSearchParams(searchParams)}`,
                      }}
                      state={{
                        spaceSlug: spaceData.space.slug,
                        backgroundLocation: location,
                      }}
                      tooltip="Search"
                    >
                      <Icon name="search" />
                    </NavLink>
                    {spaceData.space.role === 'ADMIN' && (
                      <NavLink
                        to={`/s/${spaceData.space.slug}/settings`}
                        tooltip="Settings"
                      >
                        {location.pathname.includes(
                          `/s/${spaceData.space.slug}/settings`
                        ) ? (
                          <Icon name="settings-filled" />
                        ) : (
                          <Icon name="settings" />
                        )}
                      </NavLink>
                    )}
                    <NavLink
                      to={{
                        pathname: `/s/${spaceData.space.slug}/t/new`,
                        search: `?${createSearchParams(searchParams)}`,
                      }}
                      state={{
                        spaceId: spaceData.space.id,
                        spaceSlug: spaceData.space.slug,
                        backgroundLocation: location,
                      }}
                      tooltip="New topic"
                      $nextToAvatar
                    >
                      <Icon name="create" />
                    </NavLink>
                  </>
                )}
              {viewerData && (
                <Drawer>
                  <DrawerTrigger aria-label="Account">
                    <Meta>
                      <Avatar
                        src={viewerData.viewer.avatarUrl}
                        alt={`${viewerData.viewer.name} avatar`}
                      />
                    </Meta>
                  </DrawerTrigger>
                  <DrawerList>
                    <DrawerExit />
                    <DrawerLink
                      as="button"
                      onClick={() =>
                        navigate('/keyboard-shortcuts', {
                          state: {
                            inApp: true,
                            fromSpace:
                              params.s &&
                              location.pathname.split('/').splice(1)[1],
                            fromTopic: params.t,
                            fromSettings:
                              location.pathname.split('/')[1] === 'settings',
                            backgroundLocation: location,
                          },
                        })
                      }
                    >
                      Keyboard shortcuts
                    </DrawerLink>
                    <DrawerLink to="/settings">Settings</DrawerLink>
                    <DrawerLink
                      to="/logout"
                      state={{
                        inApp: true,
                        fromPublicSpace:
                          params.s && spaceData && spaceData.space?.public,
                        backgroundLocation: location,
                      }}
                    >
                      Log out
                    </DrawerLink>
                  </DrawerList>
                </Drawer>
              )}
            </>
          )}
          {!localStorage.getItem('token') && (
            <Meta>
              <NavLink
                to="/login"
                state={{
                  from: location,
                  inApp: true,
                  backgroundLocation: location,
                }}
                tooltip="Log in"
              >
                <Icon name="in" />
              </NavLink>
            </Meta>
          )}
        </Nav>
        <Main>
          <Outlet />
        </Main>
      </Chrome>
    </SyncProvider>
  );
};

export default MainLayout;
